import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { RotateCcw, AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UserSubscription, SubscriptionStatus } from "@/types/subscription";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Define the form schema
const changeStatusSchema = z.object({
  newStatus: z.enum(["active", "cancelled", "expired", "pending"]),
  adminNotes: z.string().optional(),
});

type ChangeStatusFormValues = z.infer<typeof changeStatusSchema>;

interface ChangeSubscriptionStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: ChangeStatusFormValues) => void;
  subscription: UserSubscription;
  isProcessing: boolean;
  userName: string;
}

export default function ChangeSubscriptionStatusDialog({
  open,
  onOpenChange,
  onConfirm,
  subscription,
  isProcessing,
  userName,
}: ChangeSubscriptionStatusDialogProps) {
  const availableStatusChanges = getAvailableStatusChanges(subscription.status);

  // Initialize the form
  const form = useForm<ChangeStatusFormValues>({
    resolver: zodResolver(changeStatusSchema),
    defaultValues: {
      newStatus: availableStatusChanges[0] as
        | "active"
        | "cancelled"
        | "expired"
        | "pending",
      adminNotes: `Changing subscription status from ${subscription.status} to ${availableStatusChanges[0]} for ${userName}`,
    },
  });

  // Get impact description based on status
  const getStatusImpactDescription = (status: SubscriptionStatus) => {
    switch (status) {
      case "active":
        return "User will have full access to subscribed features";
      case "cancelled":
        return "User will have access until the subscription end date";
      case "expired":
        return "User will immediately lose access to premium features";
      case "pending":
        return "User will have no access until payment/approval";
      default:
        return "";
    }
  };

  // Get available status transitions based on current status
  function getAvailableStatusChanges(
    currentStatus: SubscriptionStatus
  ): SubscriptionStatus[] {
    switch (currentStatus) {
      case "active":
        return ["cancelled", "expired"];
      case "cancelled":
        return ["active", "expired"];
      case "expired":
        return ["active", "pending"];
      case "pending":
        return ["active", "cancelled"];
      default:
        return ["active", "cancelled", "expired", "pending"].filter(
          (status) => status !== currentStatus
        ) as SubscriptionStatus[];
    }
  }

  // Get status specific notes
  const getStatusChangeWarning = (targetStatus: SubscriptionStatus) => {
    switch (targetStatus) {
      case "active":
        return subscription.subscriptionEnd &&
          new Date(subscription.subscriptionEnd) < new Date()
          ? "This subscription's end date has already passed. Consider extending the subscription period."
          : null;
      case "expired":
        return "This will immediately terminate the user's access to premium features. This action is typically irreversible.";
      case "cancelled":
        return "This will mark the subscription as cancelled, but the user will retain access until the end date.";
      default:
        return null;
    }
  };

  // Handle form submission
  const onSubmit = (data: ChangeStatusFormValues) => {
    onConfirm(data);
  };

  // Handle status change
  const handleStatusChange = (value: SubscriptionStatus) => {
    form.setValue("newStatus", value);

    // Update admin notes
    form.setValue(
      "adminNotes",
      `Changing subscription status from ${subscription.status} to ${value} for ${userName}`
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            Change Subscription Status
          </DialogTitle>
          <DialogDescription>
            Update the status of {userName}'s subscription to{" "}
            {subscription?.subscriptionPlan?.name}.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="bg-muted/30 p-3 rounded-md">
              <p className="text-sm mb-1">
                <span className="font-medium">Current Status: </span>
                <span className="capitalize">{subscription.status}</span>
              </p>
              <p className="text-sm">
                <span className="font-medium">End Date: </span>
                <span>
                  {subscription.subscriptionEnd
                    ? format(new Date(subscription.subscriptionEnd), "PPP")
                    : "No end date (perpetual)"}
                </span>
              </p>
            </div>

            <FormField
              control={form.control}
              name="newStatus"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Status</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      handleStatusChange(value as SubscriptionStatus)
                    }
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select new status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableStatusChanges.map((status) => (
                        <SelectItem key={status} value={status}>
                          <span className="capitalize">{status}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {getStatusImpactDescription(field.value)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {getStatusChangeWarning(form.watch("newStatus")) && (
              <Alert variant="warning" className="mb-3">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  {getStatusChangeWarning(form.watch("newStatus"))}
                </AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="adminNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Admin Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter notes about this status change"
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isProcessing}>
                {isProcessing ? "Processing..." : "Change Status"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
